import React, { useEffect, useState, useMemo, useCallback } from "react";
import DataTable from "react-data-table-component";
import Header from "../Header";
import Footer from "../Footer";
import API_BASE_URL from "../../Config";

/* ────────── SVG Icon System ────────── */
const Icon = ({ d, size = 18, stroke = 1.8 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={stroke}
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ display: "block", flexShrink: 0 }}
  >
    {d}
  </svg>
);

const SearchIcon = (p) => (
  <Icon {...p} d={<><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" /></>} />
);
const CalendarIcon = (p) => (
  <Icon {...p} d={<><rect x="3" y="4" width="18" height="18" rx="2" /><path d="M16 2v4M8 2v4M3 10h18" /></>} />
);
const UserIcon = (p) => (
  <Icon {...p} d={<><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></>} />
);
const TrashIcon = (p) => (
  <Icon {...p} d={<><path d="M3 6h18M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" /></>} />
);
const ChevronLeftIcon = (p) => (
  <Icon {...p} d={<><path d="m15 18-6-6 6-6" /></>} />
);
const ChevronRightIcon = (p) => (
  <Icon {...p} d={<><path d="m9 18 6-6-6-6" /></>} />
);
const XCircleIcon = (p) => (
  <Icon {...p} d={<><circle cx="12" cy="12" r="10" /><path d="m15 9-6 6M9 9l6 6" /></>} />
);
const CheckIcon = (p) => (
  <Icon {...p} d={<><path d="M20 6 9 17l-5-5" /></>} />
);
const StethoscopeIcon = (p) => (
  <Icon {...p} d={<><path d="M4.8 2.3A.3.3 0 1 0 5 2H4a2 2 0 0 0-2 2v5a6 6 0 0 0 6 6v0a6 6 0 0 0 6-6V4a2 2 0 0 0-2-2h-1a.2.2 0 1 0 .3.3" /><path d="M8 15v1a6 6 0 0 0 6 6v0a6 6 0 0 0 6-6v-4" /><circle cx="20" cy="10" r="2" /></>} />
);
const VideoIcon = (p) => (
  <Icon {...p} d={<><path d="m22 8-6 4 6 4V8z" /><rect x="2" y="6" width="14" height="12" rx="2" /></>} />
);
const PhoneIcon = (p) => (
  <Icon {...p} d={<><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12 19.79 19.79 0 0 1 1.61 3.38 2 2 0 0 1 3.6 1.21h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L7.91 9.91a16 16 0 0 0 6.18 6.18l1.86-1.86a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" /></>} />
);
const IndianRupeeIcon = (p) => (
  <Icon {...p} d={<><path d="M6 3h12M6 8h12M6 13l8.5 8L19 13" /><path d="M6 8a4 4 0 0 0 0 5h5" /></>} />
);

/* ────────── Custom Confirm Dialog ────────── */
const ConfirmDialog = ({ open, title, message, confirmLabel = "Confirm", confirmVariant = "danger", onConfirm, onCancel }) => {
  if (!open) return null;
  return (
    <div className="cdlg-overlay" role="dialog" aria-modal="true" aria-labelledby="cdlg-title">
      <div className="cdlg-box">
        <div className={`cdlg-icon-wrap cdlg-icon-${confirmVariant}`}>
          {confirmVariant === "danger" ? (
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="3 6 5 6 21 6" />
              <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
              <path d="M10 11v6M14 11v6" />
              <path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" />
            </svg>
          ) : (
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
          )}
        </div>
        <h3 id="cdlg-title" className="cdlg-title">{title}</h3>
        <p className="cdlg-message">{message}</p>
        <div className="cdlg-actions">
          <button type="button" className="cdlg-btn cdlg-btn-cancel" onClick={onCancel}>
            Cancel
          </button>
          <button type="button" className={`cdlg-btn cdlg-btn-confirm cdlg-btn-${confirmVariant}`} onClick={onConfirm}>
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ────────── Multi-Select Dropdown ────────── */
const MultiSelect = ({ options, value, onChange, placeholder = "Filter…" }) => {
  const [open, setOpen] = useState(false);

  const toggleOption = (opt) => {
    onChange(value.includes(opt) ? value.filter((v) => v !== opt) : [...value, opt]);
  };

  const clearAll = (e) => {
    e.stopPropagation();
    onChange([]);
  };

  return (
    <div className="db-multiselect" style={{ position: "relative" }}>
      <div
        className="db-multiselect__trigger"
        onClick={() => setOpen(!open)}
        tabIndex={0}
        onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setOpen(false); }}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="db-multiselect__label">
          {value.length === 0
            ? <span style={{ color: "#9aa8a2" }}>{placeholder}</span>
            : value.map((v) => (
                <span key={v} className="db-multiselect__tag">{v}</span>
              ))}
        </span>
        <span className="db-multiselect__caret">
          {value.length > 0 && (
            <button className="db-multiselect__clear" onClick={clearAll} tabIndex={-1} type="button">
              <XCircleIcon size={14} />
            </button>
          )}
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
            strokeLinecap="round" strokeLinejoin="round"
            style={{ transform: open ? "rotate(180deg)" : "rotate(0deg)", transition: "transform .2s ease" }}>
            <path d="m6 9 6 6 6-6" />
          </svg>
        </span>
      </div>
      {open && (
        <div className="db-multiselect__dropdown" role="listbox" aria-multiselectable="true">
          {options.map((opt) => (
            <div
              key={opt}
              className={`db-multiselect__option ${value.includes(opt) ? "is-selected" : ""}`}
              role="option"
              aria-selected={value.includes(opt)}
              onClick={() => toggleOption(opt)}
              tabIndex={0}
              onKeyDown={(e) => e.key === "Enter" && toggleOption(opt)}
            >
              <span className="db-multiselect__check">
                {value.includes(opt) && <CheckIcon size={12} stroke={2.5} />}
              </span>
              {opt}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/* ────────── Custom Pagination ────────── */
const CustomPagination = ({ currentPage, totalPages, onPageChange, perPage, totalRows, onPerPageChange }) => {
  const pages = useMemo(() => {
    const arr = [];
    const delta = 2;
    const left = currentPage - delta;
    const right = currentPage + delta + 1;
    let l;
    for (let i = 1; i <= totalPages; i++) {
      if (i === 1 || i === totalPages || (i >= left && i < right)) arr.push(i);
    }
    const withEllipsis = [];
    arr.forEach((i) => {
      if (l) {
        if (i - l === 2) withEllipsis.push(l + 1);
        else if (i - l > 2) withEllipsis.push("...");
      }
      withEllipsis.push(i);
      l = i;
    });
    return withEllipsis;
  }, [currentPage, totalPages]);

  return (
    <div className="db-pagination">
      <div className="db-pagination__info">
        <span>
          Showing <b>{Math.min((currentPage - 1) * perPage + 1, totalRows)}</b>–
          <b>{Math.min(currentPage * perPage, totalRows)}</b> of <b>{totalRows}</b>
        </span>
        <select
          className="db-pagination__perpage"
          value={perPage}
          onChange={(e) => onPerPageChange(Number(e.target.value))}
        >
          {[10, 20, 50, 100].map((n) => (
            <option key={n} value={n}>{n} / page</option>
          ))}
        </select>
      </div>
      <div className="db-pagination__pages">
        <button className="db-pagination__btn" disabled={currentPage === 1} onClick={() => onPageChange(currentPage - 1)} aria-label="Previous page">
          <ChevronLeftIcon size={16} />
        </button>
        {pages.map((p, i) =>
          p === "..." ? (
            <span key={`e-${i}`} className="db-pagination__ellipsis">…</span>
          ) : (
            <button
              key={p}
              className={`db-pagination__btn ${p === currentPage ? "is-active" : ""}`}
              onClick={() => onPageChange(p)}
              aria-current={p === currentPage ? "page" : undefined}
            >
              {p}
            </button>
          )
        )}
        <button className="db-pagination__btn" disabled={currentPage === totalPages} onClick={() => onPageChange(currentPage + 1)} aria-label="Next page">
          <ChevronRightIcon size={16} />
        </button>
      </div>
    </div>
  );
};

/* ────────── Main Component ────────── */
const DoctorBookings = () => {

  const [loading, setLoading] = useState(false);
  const [doctors, setDoctors] = useState([]);
  const [filterText, setFilterText] = useState("");
  const [statusFilter, setStatusFilter] = useState([]);
  const [messagealert, setMessageAlert] = useState("");
  const [messageType, setMessageType] = useState("");

  const token = localStorage.getItem("authToken");

  // Server-side pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [totalRows, setTotalRows] = useState(0);

  /* ── Confirm dialog state ── */
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: "",
    message: "",
    confirmLabel: "Confirm",
    confirmVariant: "danger",
    onConfirm: null,
  });

  const showConfirm = useCallback(({ title, message, onConfirm, confirmLabel = "Confirm", confirmVariant = "danger" }) => {
    setConfirmDialog({ open: true, title, message, onConfirm, confirmLabel, confirmVariant });
  }, []);

  const closeConfirm = useCallback(() => {
    setConfirmDialog((prev) => ({ ...prev, open: false, onConfirm: null }));
  }, []);

  // ─── FIX: search/status now goes to the backend, not just the current
  // page. fetchDoctors previously only ever loaded `page=X&per_page=Y`,
  // so typing a search term or picking a status only ever filtered the
  // ~10 rows already on screen. ───
  const fetchDoctors = (pg = 1, size = perPage, search = filterText, status = statusFilter) => {
    setLoading(true);
    const params = new URLSearchParams({ page: pg, per_page: size });
    if (search) params.append("search", search);
    if (status.length > 0) params.append("status", status.join(","));
    fetch(`${API_BASE_URL}api/doctor-bookings?${params.toString()}`, {
      method: "GET",
      headers: {
        Accept: "application/json",
        Authorization: `Bearer ${token}`,
      },
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.status && Array.isArray(data.data)) {
          setDoctors(data.data);
          setTotalRows(data.pagination.total);
          setCurrentPage(data.pagination.current_page);
        }
      })
      .catch((err) => console.error("Error fetching doctors:", err))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchDoctors(1, perPage);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Debounced: typing a search term or changing the status filter
  // re-queries the backend from page 1 instead of only re-filtering
  // whatever happened to already be loaded.
  useEffect(() => {
    const t = setTimeout(() => { fetchDoctors(1, perPage, filterText, statusFilter); }, 350);
    return () => clearTimeout(t);
  }, [filterText, statusFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── Safety net: re-applies the same match locally on whatever came
     back. If the backend already filtered by search/status, this is a
     no-op. If /api/doctor-bookings doesn't support these params yet,
     this preserves the old (page-only) filtering as a fallback instead
     of silently showing unfiltered results. ── */
  const filteredDoctors = useMemo(() => {
    let result = doctors;
    if (filterText) {
      const search = filterText.toLowerCase();
      result = result.filter((p) =>
        p?.doctor_data?.name?.toLowerCase().includes(search) ||
        p?.booking_date?.toLowerCase().includes(search) ||
        p?.booking_time?.toLowerCase().includes(search) ||
        p?.patient_name?.toLowerCase().includes(search) ||
        p?.mobile?.toLowerCase().includes(search) ||
        p?.status?.toLowerCase().includes(search)
      );
    }
    if (statusFilter.length > 0) {
      result = result.filter((p) => statusFilter.includes(p.status));
    }
    return result;
  }, [filterText, statusFilter, doctors]);

  /* ── Server pagination handlers ── */
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    fetchDoctors(newPage, perPage, filterText, statusFilter);
  };

  const handlePerPageChange = (newSize) => {
    setPerPage(newSize);
    setCurrentPage(1);
    fetchDoctors(1, newSize, filterText, statusFilter);
  };

  const totalPages = Math.max(1, Math.ceil(totalRows / perPage));

  const handleStatusChange = (id, currentStatus) => {
    const newStatus = currentStatus === "Booked" ? "Confirmed" : "Booked";

    showConfirm({
      title: "Update Status",
      message: `Are you sure you want to change status to "${newStatus}"?`,
      confirmLabel: "Yes, Update",
      confirmVariant: "warning",
      onConfirm: () => {
        closeConfirm();
        fetch(`${API_BASE_URL}api/doctorbookings/status/${id}`, {
          method: "GET",
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${token}`,
          },
        })
          .then((res) => res.json())
          .then((data) => {
            if (data.status) {
              setMessageAlert(`Status updated to ${newStatus}`);
              setMessageType("success");
              fetchDoctors(currentPage, perPage);
            } else {
              setMessageAlert("Failed to update status.");
              setMessageType("danger");
            }
          })
          .finally(() => {
            setTimeout(() => {
              setMessageAlert("");
              setMessageType("");
            }, 4000);
          });
      },
    });
  };

  const updateStatus = (id, newStatus) => {
    showConfirm({
      title: "Update Booking Status",
      message: `Are you sure you want to change status to "${newStatus}"?`,
      confirmLabel: "Yes, Update",
      confirmVariant: "warning",
      onConfirm: () => {
        closeConfirm();
        fetch(`${API_BASE_URL}api/doctorBookingStatus/status/${id}`, {
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ status: newStatus }),
        })
          .then((res) => res.json())
          .then((data) => {
            if (data.status) {
              setMessageAlert(`Status updated to ${newStatus} successfully!`);
              setMessageType("success");
              const updated = doctors.map((item) =>
                item.id === id ? { ...item, status: newStatus } : item
              );
              setDoctors(updated);
            } else {
              setMessageAlert("Failed to update status.");
              setMessageType("danger");
            }
          })
          .catch((err) => {
            console.error("Error updating status:", err);
            setMessageAlert("Error while updating status.");
            setMessageType("danger");
          })
          .finally(() => {
            setTimeout(() => {
              setMessageAlert("");
              setMessageType("");
            }, 4000);
          });
      },
    });
  };

  const handleDelete = (id) => {
    showConfirm({
      title: "Delete Booking",
      message: "Are you sure you want to delete this doctor booking? This action cannot be undone.",
      confirmLabel: "Yes, Delete",
      confirmVariant: "danger",
      onConfirm: () => {
        closeConfirm();
        fetch(`${API_BASE_URL}api/doctorbookings/${id}`, {
          method: "GET",
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${token}`,
          },
        })
          .then((res) => res.json())
          .then((data) => {
            if (data.status) {
              setMessageAlert("Doctor Bookings deleted successfully!");
              setMessageType("success");
              fetchDoctors(currentPage, perPage);
            } else {
              setMessageAlert("Failed to delete doctor Bookings.");
              setMessageType("danger");
            }
          })
          .finally(() => {
            setTimeout(() => {
              setMessageAlert("");
              setMessageType("");
            }, 4000);
          });
      },
    });
  };

  /* ── Booking type badge helper ── */
  const getBookingTypeBadge = (row) => {
    const isVideo = row.booking_type === "video" || row.booking_type === "videocall";
    if (isVideo) {
      return (
        <span className="db-type-pill is-video">
          <VideoIcon size={12} />
          Video Call
        </span>
      );
    }
    return (
      <span className="db-type-pill is-inpatient">
        <StethoscopeIcon size={12} />
        In-Patient
      </span>
    );
  };

  /* ── Booking amount helper ── */
  const getBookingAmount = (row) => {
    const isVideo = row.booking_type === "video" || row.booking_type === "videocall";
    const price = isVideo
      ? row?.doctor_data?.videocall_price
      : row?.doctor_data?.consult_price;

    if (!price && price !== 0) return <span className="db-amount-muted">—</span>;
    return <span className="db-amount-cell">₹{price}</span>;
  };

  /* ── Status color helper ── */
  const getStatusClass = (status) => {
    switch (status) {
      case "Confirmed":
      case "Completed": return "is-confirmed";
      case "Booked": return "is-booked";
      case "Cancelled": return "is-cancelled";
      case "Not Attended": return "is-notattended";
      default: return "is-pending";
    }
  };

  /* ────────── Table Columns ────────── */
  const columns = useMemo(() => [
    {
      name: "ID",
      selector: (row) => row.id,
      cell: (row) => <span className="db-id-pill">#{row.id}</span>,
      sortable: true,
      width: "80px",
    },
    {
      name: "Doctor",
      selector: (row) => row.doctor_data?.name,
      cell: (row) => (
        <div className="db-doctor-cell">
          <div className="db-doctor-avatar">
            {row.doctor_data?.name?.charAt(0) || "D"}
          </div>
          <div className="db-doctor-info">
            <span className="db-doctor-name">{row.doctor_data?.name || "—"}</span>
            <span className="db-doctor-dept">{row.doctor_data?.department_name || row.doctor_data?.department || ""}</span>
          </div>
        </div>
      ),
      sortable: true,
      grow: 2,
      minWidth: "180px",
    },
    {
      name: "Patient",
      selector: (row) => row.patient_name,
      cell: (row) => (
        <div className="db-patient-cell">
          <span className="db-patient-name">{row.patient_name || "—"}</span>
          {row.mobile && (
            <span className="db-patient-mobile">
              <PhoneIcon size={11} />
              {row.mobile}
            </span>
          )}
        </div>
      ),
      sortable: true,
      minWidth: "160px",
    },
    {
      name: "Booking Date",
      selector: (row) => row.booking_date,
      cell: (row) => (
        <div className="db-date-cell">
          <CalendarIcon size={13} />
          <div>
            <span className="db-date-val">{row.booking_date || "—"}</span>
            {row.booking_time && <span className="db-time-val">{row.booking_time}</span>}
          </div>
        </div>
      ),
      sortable: true,
      width: "160px",
    },
    {
      name: "Type & Amount",
      cell: (row) => (
        <div className="db-type-amount-cell">
          {getBookingTypeBadge(row)}
          {getBookingAmount(row)}
        </div>
      ),
      width: "160px",
    },
    {
      name: "Status",
      selector: (row) => row.status,
      cell: (row) => (
        <select
          value={row.status}
          onChange={(e) => updateStatus(row.id, e.target.value)}
          className={`db-status-select ${getStatusClass(row.status)}`}
        >
          <option value="Pending">Pending</option>
          <option value="Booked">Booked</option>
          <option value="Cancelled">Cancelled</option>
          <option value="Not Attended">Not Attended</option>
          <option value="Completed">Completed</option>
        </select>
      ),
      sortable: true,
      width: "165px",
    },
    {
      name: "Created",
      selector: (row) => row.created_at,
      cell: (row) => (
        <span className="db-cell-muted">
          {row.created_at ? new Date(row.created_at).toLocaleDateString("en-GB") : "—"}
        </span>
      ),
      sortable: true,
      width: "115px",
    },
    {
      name: "Actions",
      cell: (row) => (
        <div className="db-row-actions">
          <button
            className="db-ico-btn delete"
            onClick={() => handleDelete(row.id)}
            title="Delete booking"
          >
            <TrashIcon size={15} />
          </button>
        </div>
      ),
      width: "90px",
      right: true,
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
    },
  ], [doctors]);

  /* ────────── DataTable Custom Styles ────────── */
  const tableStyles = {
    table: { style: { background: "transparent" } },
    headRow: {
      style: {
        background: "#f0fbf6",
        borderTopLeftRadius: "12px",
        borderTopRightRadius: "12px",
        borderBottom: "1px solid #d6efe2",
        minHeight: "52px",
      },
    },
    headCells: {
      style: {
        fontSize: "11.5px",
        fontWeight: 700,
        letterSpacing: "0.07em",
        textTransform: "uppercase",
        color: "#5b6b66",
        paddingLeft: "16px",
        paddingRight: "16px",
      },
    },
    rows: {
      style: {
        minHeight: "76px",
        fontSize: "14px",
        color: "#1f2a37",
        borderBottom: "1px solid #eef4f1 !important",
        transition: "background 0.18s ease",
      },
      highlightOnHoverStyle: {
        background: "#f6fcf9",
        borderBottomColor: "#e3f0e9",
        outline: "none",
      },
      stripedStyle: { background: "#fafefb" },
    },
    cells: {
      style: { paddingLeft: "16px", paddingRight: "16px" },
    },
    pagination: { style: { display: "none" } },
  };

  const STATUS_OPTIONS = ["Pending", "Booked", "Cancelled", "Not Attended", "Completed"];

  return (
    <>
      <style>{`
        :root {
          --db-bg:           #f3faf6;
          --db-card:         #ffffff;
          --db-border:       #e3f0e9;
          --db-text:         #0f1f1a;
          --db-muted:        #6b7a74;
          --db-primary:      #10b981;
          --db-primary-d:    #059669;
          --db-primary-grad: linear-gradient(135deg, #14b8a6 0%, #10b981 100%);
          --db-soft:         #ecfdf5;
          --db-del:          #fee4e2;
          --db-del-fg:       #e0413c;
          --db-shadow:       0 1px 2px rgba(16,30,24,.04), 0 4px 14px rgba(16,30,24,.04);
        }

        /* ── Shell ── */
        .db-shell {
          background: var(--db-bg);
          min-height: 100%;
          padding: 24px;
        }

        /* ── Page Header ── */
        .db-head {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 16px;
          flex-wrap: wrap;
          margin-bottom: 20px;
        }
        .db-head-left { display: flex; align-items: center; gap: 14px; }
        .db-head-icon {
          width: 48px; height: 48px;
          background: var(--db-primary-grad);
          border-radius: 14px;
          display: flex; align-items: center; justify-content: center;
          box-shadow: 0 6px 18px rgba(16,185,129,.28);
          flex-shrink: 0;
        }
        .db-head-icon svg { fill: none !important; color: #fff; }
        .db-head h2 {
          font-size: clamp(22px, 2.4vw, 30px);
          font-weight: 800;
          color: var(--db-text);
          margin: 0 0 4px 0;
          letter-spacing: -0.01em;
        }
        .db-head p { color: var(--db-muted); font-size: 14px; margin: 0; }

        /* ── Alert ── */
        .db-alert {
          padding: 13px 18px; border-radius: 12px;
          font-size: 14px; font-weight: 500;
          margin-bottom: 16px;
          animation: dbDropIn .3s cubic-bezier(.34,1.56,.64,1);
        }
        .db-alert-success { background: #ecfdf5; color: #065f46; border: 1px solid #b7e8d2; }
        .db-alert-danger  { background: #fff1f0; color: #991b1b; border: 1px solid #fbd7d3; }

        /* ── Card ── */
        .db-card {
          background: var(--db-card);
          border: 1px solid var(--db-border);
          border-radius: 18px;
          box-shadow: var(--db-shadow);
          overflow: hidden;
        }
        .db-card-top {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          padding: 18px 18px 14px;
          flex-wrap: wrap;
          border-bottom: 1px solid #f0f6f3;
        }
        .db-card-top-left {
          display: flex;
          align-items: center;
          gap: 10px;
          flex: 1 1 320px;
          flex-wrap: wrap;
        }

        /* ── Search ── */
        .db-search-box {
          position: relative;
          flex: 1 1 240px;
          max-width: 380px;
        }
        .db-search-box svg {
          position: absolute;
          left: 14px; top: 50%;
          transform: translateY(-50%);
          color: #9aa8a2;
          pointer-events: none;
          fill: none !important;
        }
        .db-search-box input {
          width: 100%;
          padding: 11px 14px 11px 42px;
          border-radius: 999px;
          border: 1px solid var(--db-border);
          background: #f6fbf8;
          font-size: 14px; color: var(--db-text);
          outline: none;
          transition: border-color .15s, box-shadow .15s, background .15s;
        }
        .db-search-box input::placeholder { color: #9aa8a2; }
        .db-search-box input:focus {
          border-color: var(--db-primary);
          background: #fff;
          box-shadow: 0 0 0 4px rgba(16,185,129,.12);
        }

        /* ── Total Badge ── */
        .db-total-badge {
          display: inline-flex; align-items: center; gap: 8px;
          padding: 9px 14px;
          background: #fff;
          border: 1px solid var(--db-border);
          border-radius: 999px;
          font-size: 13px; color: var(--db-text); font-weight: 600;
          white-space: nowrap;
        }
        .db-total-badge svg { fill: none !important; color: var(--db-primary); }
        .db-total-badge b  { color: var(--db-primary-d); }

        /* ── Multi-select ── */
        .db-multiselect {
          flex: 1 1 180px;
          max-width: 260px;
          z-index: 20;
        }
        .db-multiselect__trigger {
          display: flex; align-items: center; justify-content: space-between;
          gap: 8px;
          padding: 9px 12px 9px 14px;
          background: #f6fbf8;
          border: 1px solid var(--db-border);
          border-radius: 10px;
          cursor: pointer; font-size: 13.5px; color: var(--db-text);
          user-select: none;
          transition: border-color .15s, box-shadow .15s, background .15s;
          min-height: 42px;
        }
        .db-multiselect__trigger:hover,
        .db-multiselect__trigger:focus {
          border-color: var(--db-primary);
          background: #fff;
          box-shadow: 0 0 0 4px rgba(16,185,129,.12);
          outline: none;
        }
        .db-multiselect__label {
          display: flex; gap: 5px; align-items: center; flex-wrap: wrap; flex: 1;
        }
        .db-multiselect__tag {
          display: inline-flex; align-items: center;
          background: var(--db-soft); color: #047857;
          border: 1px solid #b7e8d2; border-radius: 999px;
          padding: 2px 9px; font-size: 12px; font-weight: 600;
        }
        .db-multiselect__caret {
          display: inline-flex; align-items: center; gap: 4px; color: #9aa8a2;
        }
        .db-multiselect__clear {
          display: inline-flex; align-items: center; justify-content: center;
          background: none; border: none; cursor: pointer;
          color: #9aa8a2; padding: 2px;
          transition: color .15s;
        }
        .db-multiselect__clear:hover { color: var(--db-del-fg); }
        .db-multiselect__clear svg { fill: none !important; }
        .db-multiselect__dropdown {
          position: absolute;
          top: calc(100% + 6px); left: 0; right: 0;
          background: #fff;
          border: 1px solid var(--db-border);
          border-radius: 12px;
          box-shadow: 0 12px 32px rgba(16,30,24,.12);
          overflow: hidden;
          animation: dbDropIn .18s ease;
          z-index: 100;
        }
        .db-multiselect__option {
          display: flex; align-items: center; gap: 10px;
          padding: 10px 14px; cursor: pointer;
          font-size: 13.5px; color: var(--db-text);
          transition: background .12s;
        }
        .db-multiselect__option:hover  { background: #f6fbf8; }
        .db-multiselect__option.is-selected { background: var(--db-soft); }
        .db-multiselect__check {
          width: 18px; height: 18px; border-radius: 5px;
          border: 1.5px solid #c7d3ce;
          display: inline-flex; align-items: center; justify-content: center;
          flex-shrink: 0; background: #fff; transition: all .12s;
        }
        .db-multiselect__option.is-selected .db-multiselect__check {
          background: var(--db-primary); border-color: var(--db-primary);
        }
        .db-multiselect__check svg { fill: none !important; color: #fff; }

        /* ── Cell Styles ── */
        .db-id-pill {
          display: inline-block;
          padding: 3px 9px;
          background: #eef4f1; color: #4b5b56;
          border-radius: 999px; font-size: 12px;
          font-weight: 600; letter-spacing: .02em;
          font-family: "SFMono-Regular", "Consolas", monospace;
        }

        .db-doctor-cell { display: flex; align-items: center; gap: 10px; }
        .db-doctor-avatar {
          width: 38px; height: 38px; border-radius: 50%;
          background: var(--db-primary-grad);
          color: #fff; font-size: 15px; font-weight: 700;
          display: flex; align-items: center; justify-content: center;
          flex-shrink: 0; box-shadow: 0 3px 8px rgba(16,185,129,.22);
          text-transform: uppercase;
        }
        .db-doctor-info { display: flex; flex-direction: column; gap: 2px; }
        .db-doctor-name { font-weight: 600; color: var(--db-text); font-size: 14px; }
        .db-doctor-dept { font-size: 12px; color: var(--db-muted); }

        .db-patient-cell { display: flex; flex-direction: column; gap: 3px; }
        .db-patient-name { font-weight: 600; color: var(--db-text); font-size: 14px; }
        .db-patient-mobile {
          display: inline-flex; align-items: center; gap: 4px;
          font-size: 12px; color: var(--db-muted);
        }
        .db-patient-mobile svg { fill: none !important; }

        .db-date-cell {
          display: flex; align-items: flex-start; gap: 7px;
          color: var(--db-muted);
        }
        .db-date-cell svg { fill: none !important; flex-shrink: 0; margin-top: 2px; }
        .db-date-val { display: block; font-size: 13.5px; font-weight: 600; color: var(--db-text); }
        .db-time-val { display: block; font-size: 11.5px; color: var(--db-muted); margin-top: 1px; }

        /* ── Type & Amount ── */
        .db-type-amount-cell {
          display: flex; flex-direction: column; gap: 5px; align-items: flex-start;
        }
        .db-type-pill {
          display: inline-flex; align-items: center; gap: 5px;
          padding: 3px 10px; border-radius: 999px;
          font-size: 11.5px; font-weight: 600;
          border: 1px solid transparent; user-select: none;
        }
        .db-type-pill svg { fill: none !important; }
        .db-type-pill.is-video {
          background: #eff6ff; color: #1d4ed8; border-color: #bfdbfe;
        }
        .db-type-pill.is-inpatient {
          background: #f0fdf4; color: #166534; border-color: #bbf7d0;
        }
        .db-amount-cell { font-weight: 700; color: var(--db-text); font-size: 14px; }
        .db-amount-muted { color: var(--db-muted); font-size: 13px; }

        /* ── Status Select ── */
        .db-status-select {
          appearance: none;
          padding: 6px 28px 6px 10px;
          border-radius: 8px;
          border: 1.5px solid transparent;
          font-size: 12.5px; font-weight: 600;
          cursor: pointer; outline: none;
          transition: all .15s;
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='%239aa8a2' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E");
          background-repeat: no-repeat;
          background-position: right 8px center;
        }
        .db-status-select.is-confirmed { background-color: #ecfdf5; color: #047857; border-color: #b7e8d2; }
        .db-status-select.is-booked    { background-color: #eff6ff; color: #1d4ed8; border-color: #bfdbfe; }
        .db-status-select.is-cancelled { background-color: #fff1f0; color: #c0392b; border-color: #fbd7d3; }
        .db-status-select.is-notattended { background-color: #fefce8; color: #a16207; border-color: #fde68a; }
        .db-status-select.is-pending   { background-color: #f4f4f5; color: #52525b; border-color: #d4d4d8; }
        .db-status-select:focus { box-shadow: 0 0 0 3px rgba(16,185,129,.15); }

        .db-cell-muted { color: var(--db-muted); font-size: 13px; }

        /* ── Action buttons ── */
        .db-row-actions { display: inline-flex; gap: 6px; }
        .db-ico-btn {
          width: 34px; height: 34px;
          display: inline-flex; align-items: center; justify-content: center;
          border-radius: 9px; border: none; cursor: pointer;
          transition: transform .15s, filter .15s;
        }
        .db-ico-btn svg { fill: none !important; }
        .db-ico-btn:hover { transform: translateY(-1px); filter: brightness(.93); }
        .db-ico-btn.delete { background: var(--db-del); color: var(--db-del-fg); }

        /* ── Custom Pagination ── */
        .db-pagination {
          display: flex; align-items: center; justify-content: space-between;
          gap: 12px; flex-wrap: wrap;
          padding: 14px 18px;
          border-top: 1px solid #eef4f1;
          font-size: 13px; color: var(--db-muted);
        }
        .db-pagination__info {
          display: flex; align-items: center; gap: 10px; flex-wrap: wrap;
        }
        .db-pagination__info b { color: var(--db-text); }
        .db-pagination__perpage {
          padding: 6px 10px; border-radius: 8px;
          border: 1px solid var(--db-border);
          background: #f6fbf8; font-size: 13px; color: var(--db-text);
          cursor: pointer; outline: none;
        }
        .db-pagination__perpage:focus { border-color: var(--db-primary); }
        .db-pagination__pages { display: flex; align-items: center; gap: 4px; }
        .db-pagination__btn {
          display: inline-flex; align-items: center; justify-content: center;
          min-width: 34px; height: 34px; padding: 0 6px;
          border-radius: 8px; border: 1px solid var(--db-border);
          background: #fff; font-size: 13.5px; font-weight: 500;
          color: var(--db-text); cursor: pointer;
          transition: all .15s ease;
        }
        .db-pagination__btn svg { fill: none !important; }
        .db-pagination__btn:hover:not(:disabled) {
          border-color: var(--db-primary); color: var(--db-primary); background: var(--db-soft);
        }
        .db-pagination__btn.is-active {
          background: var(--db-primary-grad); color: #fff;
          border-color: transparent;
          box-shadow: 0 4px 12px rgba(16,185,129,.25); font-weight: 700;
        }
        .db-pagination__btn:disabled { opacity: .4; cursor: not-allowed; }
        .db-pagination__ellipsis {
          display: inline-flex; align-items: center; justify-content: center;
          width: 30px; color: var(--db-muted);
        }

        /* ── Loading overlay ── */
        .db-overlay {
          position: fixed; inset: 0; z-index: 1080;
          background: rgba(255,255,255,.55);
          backdrop-filter: blur(2px);
          display: grid; place-items: center;
        }
        .db-spinner {
          width: 42px; height: 42px; border-radius: 50%;
          border: 3px solid #d6efe2;
          border-top-color: var(--db-primary);
          animation: dbSpin 0.8s linear infinite;
        }
        @keyframes dbSpin { to { transform: rotate(360deg); } }

        /* ── Empty State ── */
        .db-empty {
          padding: 48px 20px; text-align: center; color: var(--db-muted);
        }
        .db-empty svg { fill: none !important; color: #c7d3ce; margin-bottom: 12px; }
        .db-empty p { font-size: 15px; font-weight: 500; margin: 0; }
        .db-empty span { font-size: 13px; }

        /* ── Animations ── */
        @keyframes dbDropIn {
          from { opacity: 0; transform: translateY(-6px); }
          to   { opacity: 1; transform: translateY(0); }
        }

        /* ═══════════ Custom Confirm Dialog (replaces window.confirm) ═══════════ */
        .cdlg-overlay {
          position: fixed; inset: 0; z-index: 9999;
          background: rgba(15,31,26,.45);
          backdrop-filter: blur(4px);
          display: flex; align-items: center; justify-content: center;
          padding: 16px; animation: cdlgFadeIn .18s ease;
        }
        @keyframes cdlgFadeIn { from { opacity: 0; } to { opacity: 1; } }
        .cdlg-box {
          background: #fff; border-radius: 20px;
          box-shadow: 0 24px 60px rgba(15,31,26,.2), 0 4px 16px rgba(15,31,26,.08);
          padding: 32px 28px 24px;
          width: 100%; max-width: 400px;
          text-align: center;
          animation: cdlgSlideUp .22s cubic-bezier(.34,1.56,.64,1);
        }
        @keyframes cdlgSlideUp {
          from { opacity: 0; transform: translateY(18px) scale(.97); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
        .cdlg-icon-wrap {
          width: 60px; height: 60px; border-radius: 18px;
          display: flex; align-items: center; justify-content: center;
          margin: 0 auto 18px;
        }
        .cdlg-icon-wrap svg { fill: none !important; }
        .cdlg-icon-danger  { background: #fff1f0; color: #e0413c; }
        .cdlg-icon-warning { background: #fffbeb; color: #d97706; }
        .cdlg-title {
          font-size: 18px; font-weight: 800;
          color: #0f1f1a; margin: 0 0 8px; letter-spacing: -0.3px;
        }
        .cdlg-message { font-size: 14px; color: #6b7a74; line-height: 1.6; margin: 0 0 24px; }
        .cdlg-actions { display: flex; gap: 10px; justify-content: center; }
        .cdlg-btn {
          flex: 1; padding: 11px 20px; border-radius: 10px; border: none;
          font-size: 14px; font-weight: 600; cursor: pointer; transition: all .15s;
        }
        .cdlg-btn-cancel { background: #f1f5f9; color: #475569; border: 1.5px solid #e2e8f0; }
        .cdlg-btn-cancel:hover { background: #e2e8f0; color: #1e293b; }
        .cdlg-btn-confirm { color: #fff; }
        .cdlg-btn-danger  {
          background: linear-gradient(135deg, #ef4444, #f43f5e);
          box-shadow: 0 4px 14px rgba(239,68,68,.28);
        }
        .cdlg-btn-danger:hover  { box-shadow: 0 6px 20px rgba(239,68,68,.4); transform: translateY(-1px); }
        .cdlg-btn-warning {
          background: linear-gradient(135deg, #f59e0b, #d97706);
          box-shadow: 0 4px 14px rgba(245,158,11,.28);
        }
        .cdlg-btn-warning:hover { box-shadow: 0 6px 20px rgba(245,158,11,.4); transform: translateY(-1px); }

        /* ── Responsive ── */
        @media (max-width: 768px) {
          .db-shell { padding: 14px; }
          .db-head  { flex-direction: column; align-items: stretch; }
          .db-card-top { flex-direction: column; align-items: stretch; }
          .db-search-box { max-width: 100%; }
          .db-multiselect { max-width: 100%; }
          .db-pagination { flex-direction: column; align-items: center; gap: 10px; }
        }

        /* ── SVG fill override ── */
        .db-shell svg, .cdlg-overlay svg { fill: none; }
      `}</style>

      <div id="layout-wrapper">
        <Header />

        {loading && (
          <div className="db-overlay">
            <div className="db-spinner" />
          </div>
        )}

        <div className="main-content">
          <div className="page-content">
            <div className="db-shell">

              {/* ── Page Header ── */}
              <div className="db-head">
                <div className="db-head-left">
                  <div className="db-head-icon">
                    <CalendarIcon size={22} />
                  </div>
                  <div>
                    <h2>Doctor Bookings</h2>
                    <p>View and manage all doctor appointment bookings.</p>
                  </div>
                </div>
              </div>

              {/* ── Alert ── */}
              {messagealert && (
                <div className={`db-alert db-alert-${messageType}`}>
                  {messagealert}
                </div>
              )}

              {/* ── Main Card ── */}
              <div className="db-card">

                {/* Toolbar */}
                <div className="db-card-top">
                  <div className="db-card-top-left">
                    <div className="db-search-box">
                      <SearchIcon size={17} />
                      <input
                        type="text"
                        placeholder="Search doctor, patient, mobile…"
                        value={filterText}
                        onChange={(e) => setFilterText(e.target.value)}
                      />
                    </div>
                    <MultiSelect
                      options={STATUS_OPTIONS}
                      value={statusFilter}
                      onChange={setStatusFilter}
                      placeholder="Filter by status…"
                    />
                  </div>
                  <div className="db-total-badge">
                    <StethoscopeIcon size={16} />
                    Total:&nbsp;<b>{totalRows}</b>
                  </div>
                </div>

                {/* Table */}
                <DataTable
                  columns={columns}
                  data={filteredDoctors}
                  customStyles={tableStyles}
                  highlightOnHover
                  responsive
                  striped
                  noDataComponent={
                    <div className="db-empty">
                      <CalendarIcon size={40} />
                      <p>No bookings found</p>
                      <span>Try adjusting your search or filters</span>
                    </div>
                  }
                />

                {/* Custom Pagination */}
                {totalRows > 0 && (
                  <CustomPagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={handlePageChange}
                    perPage={perPage}
                    totalRows={totalRows}
                    onPerPageChange={handlePerPageChange}
                  />
                )}

              </div>
            </div>
          </div>
        </div>

        {/* ────────── Custom Confirm Dialog (replaces window.confirm) ────────── */}
        <ConfirmDialog
          open={confirmDialog.open}
          title={confirmDialog.title}
          message={confirmDialog.message}
          confirmLabel={confirmDialog.confirmLabel}
          confirmVariant={confirmDialog.confirmVariant}
          onConfirm={confirmDialog.onConfirm}
          onCancel={closeConfirm}
        />

        <Footer />
      </div>
    </>
  );
};

export default DoctorBookings;